import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X } from 'lucide-react';

const navigation = [
  { name: 'Home', path: '/' },
  { name: 'Digital', path: '/digital' },
  { name: 'Interiors', path: '/interiors' },
  { name: 'About', path: '/about' },
  { name: 'Insights', path: '/blog' },
  { name: 'Contact', path: '/contact' },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  return (
    <>
      {/* Header */}
      <motion.header 
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
          isScrolled 
            ? 'py-3 bg-white/90 backdrop-blur-xl shadow-lg shadow-orange-500/5' 
            : 'py-5 bg-transparent'
        }`}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0" data-testid="header-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_growthcollective/artifacts/ejzrxoai_Footer.png" 
                alt="Buzz Craft Maven" 
                className="h-12 md:h-14 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    data-testid={`nav-${item.name.toLowerCase()}`}
                    className="relative px-4 py-2 group"
                  >
                    <span className={`text-sm font-medium transition-colors ${
                      isActive ? 'text-[#FF6B35]' : 'text-[#5A5A5A] group-hover:text-[#FF6B35]'
                    }`}>
                      {item.name}
                    </span>
                    {isActive && (
                      <motion.span
                        layoutId="activeNavFresh"
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-1 bg-gradient-to-r from-[#FF6B35] to-[#FFB627] rounded-full"
                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* CTA Button - Desktop */}
            <div className="hidden lg:block flex-shrink-0">
              <Link to="/contact" data-testid="header-cta">
                <motion.button
                  className="btn-sunrise text-sm px-6 py-2.5"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>Let's Create</span>
                </motion.button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-[#2D2D2D]"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu - Full Screen with Sunrise Effect */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="fixed inset-0 z-[99] lg:hidden bg-sunrise-hero"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Sun Glow */}
            <div className="absolute top-20 right-10 w-[300px] h-[300px] sun-orb sun-orb-primary opacity-50 animate-gentle-pulse" />
            
            <div className="flex flex-col items-center justify-center h-full pt-20">
              <nav className="flex flex-col items-center gap-6">
                {navigation.map((item, index) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      transition={{ delay: index * 0.08, duration: 0.4 }}
                    >
                      <Link
                        to={item.path}
                        className={`font-display text-4xl md:text-5xl font-bold transition-colors ${
                          isActive ? 'text-[#FF6B35]' : 'text-[#2D2D2D]/60 hover:text-[#FF6B35]'
                        }`}
                      >
                        {item.name}
                      </Link>
                    </motion.div>
                  );
                })}
              </nav>
              
              <motion.div
                className="mt-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Link to="/contact">
                  <button className="btn-sunrise px-8 py-4 text-lg">
                    <span>Let's Create</span>
                  </button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
